import styled from "styled-components";
import { Flex, Icon, P, Small } from "orcs-design-system";

import type { GuardrailCheck } from "./RequisitionGuardrailOutcome";

type CheckTone = "pass" | "warning" | "hard";

/**
 * A routing rule that did not match did nothing to the chain. The row says
 * only "Did not pass" under a green check, so it is noise. A match stays: it
 * explains the added or skipped step or the fast-track.
 */
const isRoutingMiss = (check: GuardrailCheck): boolean => {
  if (check.state !== "FAIL") {
    return false;
  }
  const effect = check.effect ?? "BLOCK";
  switch (effect) {
    case "BLOCK":
    case "WARN":
      return false;
    case "ADD_APPROVAL_STEP":
    case "SKIP_APPROVAL_STEP":
    case "AUTO_APPROVE":
      return true;
    default: {
      const unexpected: never = effect;
      return unexpected;
    }
  }
};

const visibleChecks = (checks: GuardrailCheck[]): GuardrailCheck[] =>
  checks.filter((check) => !isRoutingMiss(check));

const checkTone = (check: GuardrailCheck): CheckTone => {
  if (check.state === "PASS") {
    return "pass";
  }
  const effect = check.effect ?? "BLOCK";
  switch (effect) {
    case "WARN":
      return "warning";
    case "BLOCK":
      return "hard";
    case "ADD_APPROVAL_STEP":
    case "SKIP_APPROVAL_STEP":
    case "AUTO_APPROVE":
      return "pass";
    default: {
      const unexpected: never = effect;
      return unexpected;
    }
  }
};

const severityOf = (
  tone: CheckTone
): {
  icon: string;
  variant: "success" | "warning" | "danger";
  label: string;
} => {
  switch (tone) {
    case "pass":
      return { icon: "check", variant: "success", label: "Pass" };
    case "warning":
      return {
        icon: "exclamation-triangle",
        variant: "warning",
        label: "Warning",
      };
    case "hard":
      return { icon: "circle-xmark", variant: "danger", label: "Blocking" };
    default: {
      const unexpected: never = tone;
      return unexpected;
    }
  }
};

const toneBackground = (
  tone: CheckTone
): "warningLightest" | "dangerLightest" | undefined => {
  switch (tone) {
    case "warning":
      return "warningLightest";
    case "hard":
      return "dangerLightest";
    case "pass":
      return undefined;
    default: {
      const unexpected: never = tone;
      return unexpected;
    }
  }
};

/** Rule name. A deleted rule keeps its id. Never the category word. */
const checkLabel = (check: GuardrailCheck): string =>
  check.name?.trim() || check.ruleId;

const stripFailPrefix = (detail: string, name: string): string => {
  if (name === "") {
    return detail;
  }
  const heads = [`${name}: fail — `, `${name}: fail - `, `${name}: fail`];
  for (const head of heads) {
    if (detail.startsWith(head)) {
      return detail.slice(head.length).trim();
    }
  }
  return detail;
};

/** "FTE cap: pass" is a stub. A headcount sentence is evidence and stays. */
const isPassStub = (detail: string): boolean =>
  /^pass$/i.test(detail) || /:\s*pass$/i.test(detail);

const checkBody = (check: GuardrailCheck): string => {
  const detail = check.detail.trim();
  const description = check.description?.trim() ?? "";
  const name = check.name?.trim() ?? "";
  switch (check.state) {
    case "PASS":
      if (description) {
        return description;
      }
      return detail === "" || isPassStub(detail) ? "Passed" : detail;
    case "FAIL":
      return stripFailPrefix(detail, name) || "Did not pass";
    case "UNKNOWN":
      return detail || description;
    default: {
      const unexpected: never = check.state;
      return unexpected;
    }
  }
};

const bodyColor = (tone: CheckTone): "warningDarker" | undefined => {
  switch (tone) {
    case "warning":
      return "warningDarker";
    case "pass":
    case "hard":
      return undefined;
    default: {
      const unexpected: never = tone;
      return unexpected;
    }
  }
};

const CheckList = styled(Flex)`
  list-style: none;
`;

const CheckRow = ({ check }: { check: GuardrailCheck }) => {
  const tone = checkTone(check);
  const severity = severityOf(tone);
  return (
    <Flex
      as="li"
      alignItems="flex-start"
      gap="s"
      p="s"
      borderRadius="2"
      bg={toneBackground(tone)}
      data-testid={`guardrail-check-${check.ruleId}`}
      data-highlight={tone}
    >
      <Icon
        icon={severity.icon}
        variant={severity.variant}
        fixedWidth
        role="img"
        aria-label={severity.label}
      />
      <Flex flexDirection="column" gap="xs" flex="1" minWidth="0">
        <Small uppercase color="greyDark">
          {checkLabel(check)}
        </Small>
        <P m="0" color={bodyColor(tone)}>
          {checkBody(check)}
        </P>
      </Flex>
    </Flex>
  );
};

const GuardrailCheckList = ({ checks }: { checks: GuardrailCheck[] }) => {
  const shown = visibleChecks(checks);
  if (shown.length === 0) {
    return null;
  }
  return (
    <CheckList as="ul" flexDirection="column" gap="xs" m="0" p="0">
      {shown.map((check) => (
        <CheckRow key={check.ruleId} check={check} />
      ))}
    </CheckList>
  );
};

export default GuardrailCheckList;
